/**
 * Product card used in the home grids, the products/category listings and the
 * related-products rails. The image holder links to the product detail page;
 * the add-to-cart button adds the first variant to the client cart and opens
 * the drawer. The hover image crossfade is wired globally in SiteAnimations.
 */
import type { FC, FormEvent } from 'react'
import type { Product } from '@/types'

import Link from 'next/link'

import { useCart } from '@/lib/cart'
import { formatPrice } from '@/lib/format'

interface ProductCardProps {
    product: Product
}

export const ProductCard: FC<ProductCardProps> = ({ product }) => {
    const cart = useCart()

    const primary = product.images[0] || product.mainImage
    const secondary = product.images[1] || primary
    const variant = product.variants[0]

    const onAdd = (event: FormEvent) => {
        event.preventDefault()
        if (!variant) return

        cart.addItem({
            productHandle: product.handle,
            variantId: variant.id,
            name: product.name,
            image: primary,
            price: variant.price || product.price,
            option1: variant.option1Value || '',
            option2: variant.option2Value || '',
            quantity: 1
        })
        cart.openCart()
    }

    return (
        <div role='listitem' className='product-card w-dyn-item'>
            <div className='product-card-container'>
                <Link href={`/product/${product.handle}`} className='product-card-image-holder w-inline-block'>
                    <img src={primary} loading='lazy' alt={product.name} className='product-card-image' />
                    <img src={secondary} loading='lazy' alt='' className='secondary-product-card-image' />
                </Link>
                <div className='product-card-details-container'>
                    <div className='product-price-container'>
                        <div className='product-name'>{product.name}</div>
                        <div className='product-price'>{formatPrice(product.price)}</div>
                    </div>
                    <div className='product-add-to-card-container'>
                        <div>
                            <form className='w-commerce-commerceaddtocartform' onSubmit={onAdd}>
                                <input
                                    type='submit'
                                    className='w-commerce-commerceaddtocartbutton add-to-cart-icon'
                                    value='ㅤ'
                                    aria-label={`Add ${product.name} to cart`}
                                />
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
